import React, { useState, useCallback, useEffect } from 'react';
import ImageViewer from './ImageViewer';
import ImageLoader from './ImageLoader';
import { motion } from 'framer-motion';

interface ClickableImageProps {
  src: string;
  alt: string;
  title?: string;
  description?: string;
  className?: string;
  aspectRatio?: string;
  rounded?: boolean;
  hoverEffect?: boolean;
}

const ClickableImage: React.FC<ClickableImageProps> = ({
  src,
  alt,
  title,
  description,
  className = '',
  aspectRatio = '16/9',
  rounded = true,
  hoverEffect = true
}) => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Détecter si l'appareil est mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const openViewer = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Empêcher la propagation du clic
    setIsViewerOpen(true);
  }, []);

  const closeViewer = useCallback(() => {
    setIsViewerOpen(false);
  }, []);

  // Construire l'objet d'image pour le visualiseur
  const viewerImage = {
    src,
    alt,
    title: title || alt,
    description
  };

  // Construire les classes CSS pour l'aspect ratio
  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case '1/1': return 'aspect-square';
      case '4/3': return 'aspect-[4/3]';
      case '3/4': return 'aspect-[3/4]';
      case '16/9': return 'aspect-video';
      case '9/16': return 'aspect-[9/16]';
      case 'auto': return ''; // Pas de contrainte d'aspect
      default: return `aspect-[${aspectRatio}]`;
    }
  };

  return (
    <>
      <motion.div 
        className={`relative overflow-hidden cursor-zoom-in ${getAspectRatioClass()} ${rounded ? 'rounded-lg' : ''} ${className}`}
        whileHover={!isMobile && hoverEffect ? { scale: 1.02 } : {}}
        transition={{ duration: 0.3 }}
        onClick={openViewer}
        role="button"
        tabIndex={0}
        aria-label={`Voir l'image ${title || alt}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openViewer(e as unknown as React.MouseEvent<HTMLDivElement>);
          }
        }}
      >
        <div className={`h-full w-full ${hoverEffect ? 'group' : ''}`}>
          <ImageLoader 
            src={src} 
            alt={alt} 
            className={`w-full h-full object-cover ${hoverEffect ? 'transition-transform duration-500 group-hover:scale-110' : ''}`}
          />
          
          {/* Overlay au survol avec texte - visible par défaut sur mobile */}
          {(title || description) && hoverEffect && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:opacity-0 opacity-100 md:group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-2 sm:p-3">
              {title && <span className="text-white font-medium text-sm md:text-base">{title}</span>}
              {description && <span className="text-white/80 text-xs md:text-sm">{description}</span>}
            </div>
          )}
          
          {/* Icône de zoom - seulement visible sur desktop */}
          {hoverEffect && !isMobile && (
            <div className="absolute top-2 right-2 bg-white/80 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </div>
          )}
          
          {/* Indicateur tactile pour mobile */}
          {isMobile && (
            <div className="absolute top-2 right-2 bg-white/80 rounded-full p-1 flex items-center justify-center w-6 h-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M9 16h.01M15 16h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          )}
        </div>
      </motion.div>

      {/* Visualiseur d'image */}
      <ImageViewer 
        isOpen={isViewerOpen}
        onClose={closeViewer}
        image={viewerImage}
        showNavigation={false}
      />
    </>
  );
};

export default ClickableImage; 